import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Loader2, Zap } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const scoreColor = (s) => s >= 90 ? "bg-emerald-100 text-emerald-700" : s >= 75 ? "bg-blue-100 text-blue-700" : s >= 60 ? "bg-amber-100 text-amber-700" : "bg-slate-100 text-slate-500";
const inr = (n) => `₹${new Intl.NumberFormat("en-IN").format(n || 0)}`;

export function MatchesModal({ mode, source, onClose }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState("");
  const [created, setCreated] = useState({});

  useEffect(() => {
    const url = mode === "machines" ? `/admin/requirements/${source.id}/matches` : `/admin/machines/${source.id}/buyers`;
    api.get(url).then((r) => setData(r.data)).catch(() => toast.error("Failed to load matches")).finally(() => setLoading(false));
  }, []);

  const createLead = async (m) => {
    const key = m.machine_id || m.requirement_id;
    setCreating(key);
    try {
      const buyer_id = mode === "machines" ? data.requirement.buyer_id : m.buyer_id;
      const machine_id = mode === "machines" ? m.machine_id : source.id;
      const requirement_id = mode === "machines" ? data.requirement.id : m.requirement_id;
      const { data: lead } = await api.post("/admin/leads", { buyer_id, machine_id, requirement_id, match_score: m.score });
      toast.success(`Lead ${lead.lead_code} created`);
      setCreated((c) => ({ ...c, [key]: true }));
    } catch { toast.error("Failed to create lead"); }
    finally { setCreating(""); }
  };

  const matches = data?.matches || [];
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="matches-modal" className="max-h-[90vh] max-w-2xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display text-xl"><Zap className="text-orange" size={18} /> {mode === "machines" ? "Matching Machines" : "Matching Buyers"}</DialogTitle>
        </DialogHeader>
        <p className="text-sm text-slate-500">{mode === "machines" ? "For requirement: " : "For machine: "}{source.label}</p>
        {loading ? <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-slate-400" /></div> : (
          <div className="space-y-2">
            {matches.length === 0 && <p className="py-10 text-center text-slate-500">No relevant matches found.</p>}
            {matches.map((m) => {
              const key = m.machine_id || m.requirement_id;
              return (
                <div key={key} data-testid={`match-row-${key}`} className="rounded-lg border border-slate-200 p-3">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <span className={`rounded-full px-2 py-0.5 text-[11px] font-bold ${scoreColor(m.score)}`}>{m.score}% · {m.label}</span>
                      {mode === "machines" ? (
                        <>
                          <p className="mt-1 font-semibold text-slate-900">{m.name} <span className="font-mono text-xs text-slate-400">{m.machine_code}</span></p>
                          <p className="text-sm text-slate-500">{m.brand} {m.model} • {m.year} • {m.condition} • {inr(m.price)} • {m.city}</p>
                          <p className="text-xs text-slate-400">Seller: {m.seller_name}</p>
                        </>
                      ) : (
                        <>
                          <p className="mt-1 font-semibold text-slate-900">{m.buyer_name} <span className="font-mono text-xs text-slate-400">{m.buyer_code}</span></p>
                          <p className="text-sm text-slate-500">{m.machine_name} • Budget {inr(m.budget_min)}–{inr(m.budget_max)} • {m.preferred_location}</p>
                          <p className="text-xs text-slate-400">📞 {m.buyer_mobile} • {m.urgency}</p>
                        </>
                      )}
                      <div className="mt-1 flex flex-wrap gap-1">{m.reasons.map((r, i) => <span key={i} className="rounded bg-slate-50 px-1.5 py-0.5 text-[10px] text-slate-500">{r}</span>)}</div>
                    </div>
                    <button data-testid={`create-lead-${key}`} onClick={() => createLead(m)} disabled={creating === key || created[key]}
                      className="shrink-0 rounded-full bg-brand px-3 py-1.5 text-xs font-bold text-white disabled:opacity-60">
                      {created[key] ? "Lead ✓" : creating === key ? "..." : "Create Lead"}
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
